/** Group endpoint calls (Phase B1) — CRUD, membership, group-scoped badges, group feed. */
import type { Group, GroupMember, GroupMemberStatus, FeedItem, PaginatedResponse, Region, Language, ActiveLanguage, MapPinsResponse } from '@olimhub/shared'
import { apiClient } from '../client'

export interface CreateGroupInput {
  name: string
  description?: string
  isOpen?: boolean
  region?: Region
  locationText?: string
  // Client-resolved coordinates (Google Places pick) — when present, the
  // server trusts them directly and skips the async geocode job.
  latitude?: number
  longitude?: number
  categoryId?: string
}

export interface ListGroupsParams {
  region?: Region
  isOpen?: boolean
  mine?: boolean
  cursor?: string
  // Name search — powers the Search screen's Group facet picker.
  q?: string
  // Category facet (spec-search-fase3-groups.md).
  category?: string
  // Location facet (spec-search-localizacao-event-action-groups.md) —
  // independent of `region`, combinable with it. 'profile' needs no
  // reference point (server resolves it from the JWT).
  locationMode?: 'profile' | 'address' | 'map_center'
  radiusKm?: number
  referenceLat?: number
  referenceLng?: number
}

export interface InviteMemberInput {
  accountId?: string
  email?: string
}

export interface RespondMembershipInput {
  accept: boolean
  accountId?: string
}

export interface UpdateGroupInput {
  name?: string
  description?: string | null
  avatarUrl?: string | null
  headerUrl?: string | null
  links?: Record<string, string> | null
  region?: Region | null
  locationText?: string | null
  // Client-resolved coordinates (Google Places pick) — when present, the
  // server trusts them directly and skips the async geocode job.
  latitude?: number
  longitude?: number
  isOpen?: boolean
  categoryId?: string | null
}

export const groupsApi = {
  // language: resolves the category's display name (a group has no
  // language of its own, unlike a post) — defaults server-side to 'en'
  // when omitted, so existing callers that don't need the category name
  // yet don't have to pass it.
  create: (body: CreateGroupInput, language?: ActiveLanguage) =>
    apiClient.post<Group>('/groups', body, { params: { language } }).then((r) => r.data),

  list: (params?: ListGroupsParams, language?: ActiveLanguage) =>
    apiClient.get<PaginatedResponse<Group>>('/groups', { params: { ...params, language } }).then((r) => r.data),

  getMapPins: (params?: ListGroupsParams) =>
    apiClient.get<MapPinsResponse>('/groups/map', { params }).then((r) => r.data),

  getBySlug: (slug: string, language?: ActiveLanguage) =>
    apiClient.get<Group>(`/groups/${slug}`, { params: { language } }).then((r) => r.data),

  update: (slug: string, body: UpdateGroupInput, language?: ActiveLanguage) =>
    apiClient.patch<Group>(`/groups/${slug}`, body, { params: { language } }).then((r) => r.data),

  getFeed: (slug: string, cursor?: string, languages?: Language[], types?: string[]) =>
    apiClient
      .get<PaginatedResponse<FeedItem>>(`/groups/${slug}/feed`, {
        params: {
          cursor,
          ...(languages?.length ? { languages: languages.join(',') } : {}),
          ...(types?.length ? { types: types.join(',') } : {}),
        },
      })
      .then((r) => r.data),

  getMembers: (slug: string, status?: GroupMemberStatus) =>
    apiClient.get<GroupMember[]>(`/groups/${slug}/members`, { params: { status } }).then((r) => r.data),

  invite: (slug: string, body: InviteMemberInput) =>
    apiClient.post(`/groups/${slug}/invite`, body).then((r) => r.data),

  requestJoin: (slug: string) =>
    apiClient.post(`/groups/${slug}/request-join`).then((r) => r.data),

  respond: (slug: string, body: RespondMembershipInput) =>
    apiClient.post(`/groups/${slug}/respond`, body).then((r) => r.data),

  leave: (slug: string) =>
    apiClient.post(`/groups/${slug}/leave`).then((r) => r.data),

  grantBadge: (slug: string, accountId: string, badgeSlug: 'group_admin' | 'group_editor') =>
    apiClient.post(`/groups/${slug}/badges/grant`, { accountId, badgeSlug }).then((r) => r.data),

  revokeBadge: (slug: string, accountBadgeId: string) =>
    apiClient.delete(`/groups/${slug}/badges/${accountBadgeId}`).then((r) => r.data),
}
